import React, { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import Callout from 'components/Callout';
import Message from 'components/Message';
import Spinner from 'components/Spinner';
import { BackNavLink, Title } from 'features/App';
import { bem } from 'helpers/class-name';
import { makePath } from 'helpers/link-generation';
import { selectActiveVenue, selectRouteParams } from 'selectors/general';

import { toComparableId } from 'features/Sku/Items/SkuMenuData.normalizers';
import { SKU_MODIFIERS_TAB, SKU_TAB_PARAM } from 'features/Sku/Items/useSkuMenuState';

import ModifierGroupEditor, { MODIFIER_EDITOR_MODE } from './ModifierGroupEditor';
import useModifierAnalytics from './Modifiers.analytics';
import useModifierGroups from './useModifierGroups';
import useModifierItemCatalogue from './useModifierItemCatalogue';
import useModifiersReadOnly from './useModifiersReadOnly';
import messages from './ModifierGroupEditor.messages';
import './ModifierEditorPage.scss';

const { block, element } = bem('modifier-editor-page');

const TITLES = {
  [MODIFIER_EDITOR_MODE.CREATE]: messages.createTitle,
  [MODIFIER_EDITOR_MODE.EDIT]: messages.editTitle,
  [MODIFIER_EDITOR_MODE.READ_ONLY]: messages.readOnlyTitle,
};

export const getEditorMode = ({ groupId, isIntegrated }) => {
  if (!groupId) return MODIFIER_EDITOR_MODE.CREATE;
  return isIntegrated ? MODIFIER_EDITOR_MODE.READ_ONLY : MODIFIER_EDITOR_MODE.EDIT;
};

const ModifierEditorPage = () => {
  const navigate = useNavigate();
  const { venueId } = useSelector(selectRouteParams);
  // The store's copy of the params is written by an effect, so it still describes the previous
  // page on the first render after a navigation. The URL is right straight away, and mounting the
  // editor before its group is known would leave the form permanently blank.
  const { groupId } = useParams();
  const { seed = null, duplicatedFrom = null } = useLocation().state || {};
  const { isReadOnly: isIntegrated } = useModifiersReadOnly();
  const venueIsResolved = Boolean(useSelector(selectActiveVenue)?.venueId);

  const groupsQuery = useModifierGroups(venueId);
  const { items } = useModifierItemCatalogue(venueId);

  const libraryPath = `${makePath.items(venueId)}?${SKU_TAB_PARAM}=${SKU_MODIFIERS_TAB}`;
  const returnToLibrary = useCallback(() => navigate(libraryPath), [navigate, libraryPath]);

  const mode = getEditorMode({ groupId, isIntegrated });
  // Two traps around the groups cache: on entry the list can be mid-refetch and predate this
  // group (venue switch, save invalidation), so hold the spinner and never declare the group
  // missing until the query settles; but once the editor has mounted, a background refetch
  // must not unmount it and discard edits.
  const hasMountedEditorRef = useRef(false);
  const group = groupId
    ? (groupsQuery.data || []).find(candidate => toComparableId(candidate.core.id) === toComparableId(groupId))
    : null;
  const isSettled = !groupsQuery.isLoading && !groupsQuery.isFetching && !groupsQuery.isPreviousData;
  const loadFailed = Boolean(groupId) && groupsQuery.isError;
  const isLoadingGroup = Boolean(groupId) && !isSettled && !loadFailed && !hasMountedEditorRef.current;
  if (!isLoadingGroup && (group || !groupId)) hasMountedEditorRef.current = true;
  const isMissing = Boolean(groupId) && isSettled && !loadFailed && !group;
  // The route's gate passes anyone who owns some manual venue, not this one, so authoring is
  // checked here against the venue in the URL, once that venue is actually known.
  const cannotAuthor = !groupId && venueIsResolved && isIntegrated;
  const sendToLibrary = isMissing || cannotAuthor;
  // The group's name only reaches the heading once the list has resolved it, so the generic title
  // covers the load.
  const titleMessage = group && mode === MODIFIER_EDITOR_MODE.EDIT ? messages.editTitleNamed : TITLES[mode];

  const { trackEditorOpened } = useModifierAnalytics({ venueId });
  const hasReportedOpenRef = useRef(false);
  useEffect(() => {
    if (!venueIsResolved || sendToLibrary || hasReportedOpenRef.current) return;
    if (groupId && !isSettled) return;
    hasReportedOpenRef.current = true;
    trackEditorOpened({ mode, groupId: groupId || null });
  }, [venueIsResolved, sendToLibrary, isSettled, mode, groupId, trackEditorOpened]);

  useEffect(() => {
    if (sendToLibrary) navigate(libraryPath, { replace: true });
  }, [sendToLibrary, libraryPath, navigate]);

  if (sendToLibrary) return null;

  return (
    <div className={block()}>
      <Title>
        <BackNavLink to={libraryPath} />
        <Message message={titleMessage} values={{ groupName: group?.core?.name }} />
      </Title>

      {loadFailed && (
        <Callout className={element('load-error')} variant="error">
          <Message as="text" message={messages.loadFailed} />
        </Callout>
      )}

      {isLoadingGroup ? (
        <div className={element('loading')}>
          <Spinner />
        </div>
      ) : (
        <ModifierGroupEditor
          venueId={venueId}
          mode={mode}
          group={group}
          seed={groupId ? null : seed}
          duplicatedFrom={groupId ? null : duplicatedFrom}
          items={items}
          onCancel={returnToLibrary}
          onSaved={returnToLibrary}
        />
      )}
    </div>
  );
};

export default ModifierEditorPage;
